import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash'
import format from 'date-fns/format'
import addMonths from 'date-fns/add_months';
import subMonths from 'date-fns/sub_months';
import addDays from "date-fns/add_days";
import addWeeks from 'date-fns/add_weeks'
import startOfQuarter from 'date-fns/start_of_quarter'
import addQuarters from 'date-fns/add_quarters'
import { subDays } from 'date-fns';
import axios from 'axios';
import { getCompany } from './core/cacheService'

const ALL_TEAMS_AND_COHORTS = 'All Teams and Cohorts'

const normalizeEmail = (email: any): string => {
  if (!email || typeof email !== 'string') {
    return ''
  }
  return email.trim().toLowerCase()
}

const decodeEmailKey = (emailKey: string): string => {
  return emailKey.replace(/\|\|/g, '.')
}

const resolveDateRange = (value: any): { startAt: string; endAt: string } => {
  if (value && value.startDate && value.endDate) {
    const startDate = typeof value.startDate === 'string'
      ? value.startDate
      : format(new Date(value.startDate), 'YYYY-MM-DD')
    const endDate = typeof value.endDate === 'string'
      ? value.endDate
      : format(new Date(value.endDate), 'YYYY-MM-DD')
    return {
      startAt: `${startDate} 00:00`,
      endAt: `${endDate} 23:59`,
    }
  }

  if (value && value.start && value.end && typeof value.start === 'string' && typeof value.end === 'string' && value.start.includes('-') && value.end.includes('-')) {
    const startDate = value.start.slice(0, 10)
    const endDate = value.end.slice(0, 10)
    return {
      startAt: `${startDate} 00:00`,
      endAt: `${endDate} 23:59`,
    }
  }

  const monthValue = value && value.value ? value.value : format(new Date(), 'YYYY-MM')
  return {
    startAt: `${monthValue}-01 00:00`,
    endAt: `${monthValue}-31 23:59`,
  }
}

const getScopeType = (scopeKey: string, rootState: any): 'all' | 'team' | 'cohort' => {
  if (!scopeKey || scopeKey === ALL_TEAMS_AND_COHORTS) {
    return 'all'
  }
  if (rootState && rootState.main && rootState.main.allTeam && rootState.main.allTeam[scopeKey]) {
    return 'team'
  }
  if (rootState && rootState.leaderboards && rootState.leaderboards.cohortUser && rootState.leaderboards.cohortUser[scopeKey]) {
    return 'cohort'
  }
  return 'all'
}

const collectTeamIds = (scopeKey: string, organization: any): string[] => {
  const selected: { [key: string]: boolean } = {}
  const queue: string[] = [scopeKey]
  while (queue.length > 0) {
    const teamId = queue.shift()
    if (!teamId || selected[teamId]) {
      continue
    }
    selected[teamId] = true
    const children = organization && organization[teamId] ? Object.keys(organization[teamId]) : []
    children.forEach((childId: string) => {
      if (!selected[childId]) {
        queue.push(childId)
      }
    })
  }
  return Object.keys(selected)
}

const buildAllowedEmailSet = (scopeKey: string, rootState: any): Set<string> | null => {
  const scopeType = getScopeType(scopeKey, rootState)
  if (scopeType === 'all') {
    return null
  }

  const allowed = new Set<string>()
  if (scopeType === 'cohort') {
    const cohortUsers = rootState && rootState.leaderboards && rootState.leaderboards.cohortUser && rootState.leaderboards.cohortUser[scopeKey]
      ? rootState.leaderboards.cohortUser[scopeKey]
      : []
    cohortUsers.forEach((entry: any) => {
      if (typeof entry === 'string') {
        const email = normalizeEmail(entry)
        if (email) {
          allowed.add(email)
        }
      } else if (entry && entry.email) {
        const email = normalizeEmail(entry.email)
        if (email) {
          allowed.add(email)
        }
      }
    })
    return allowed
  }

  const teamIds = collectTeamIds(scopeKey, rootState && rootState.main && rootState.main.organization ? rootState.main.organization : {})
  const allUser = rootState && rootState.main && rootState.main.allUser ? rootState.main.allUser : {}
  for (const key in allUser) {
    const user = allUser[key]
    if (!user || !user.team || !teamIds.includes(user.team)) {
      continue
    }
    const email = user.email ? normalizeEmail(user.email) : normalizeEmail(decodeEmailKey(key))
    if (email) {
      allowed.add(email)
    }
  }
  return allowed
}

const extractRecordEmails = (record: any): string[] => {
  const emails: string[] = []
  const addEmail = (email: any) => {
    const normalized = normalizeEmail(email)
    if (normalized) {
      emails.push(normalized)
    }
  }

  addEmail(record && record.sender_email ? record.sender_email : '')
  if (record && typeof record.send_to_email === 'string') {
    record.send_to_email.split(',').forEach((email: string) => addEmail(email))
  } else if (record && Array.isArray(record.send_to_email)) {
    record.send_to_email.forEach((email: string) => addEmail(email))
  }
  if (record && record.profileimage && typeof record.profileimage === 'object') {
    Object.keys(record.profileimage).forEach((key) => {
      addEmail(key.includes('||') ? decodeEmailKey(key) : key)
    })
  }

  return _.uniq(emails)
}

const filterRecordsByScope = (records: Array<any>, allowedEmailSet: Set<string> | null): Array<any> => {
  if (!allowedEmailSet) {
    return records
  }
  if (allowedEmailSet.size === 0) {
    return []
  }
  return records.filter((record: any) => {
    const recordEmails = extractRecordEmails(record)
    return recordEmails.some((email) => allowedEmailSet.has(email))
  })
}

const filterUsersByScope = (users: Array<any>, allowedEmailSet: Set<string> | null): Array<any> => {
  if (!Array.isArray(users)) {
    return []
  }
  if (!allowedEmailSet) {
    return users || []
  }
  if (allowedEmailSet.size === 0) {
    return []
  }
  return users.filter((entry: any) => {
    if (typeof entry === 'string') {
      return allowedEmailSet.has(normalizeEmail(entry))
    }
    if (entry && entry.email) {
      return allowedEmailSet.has(normalizeEmail(entry.email))
    }
    return false
  })
}
export default {
    namespaced: true,
    state: {
      activity:[],
      activityall:[],
      page:1,
      size_page:10,
      results: 0,
      list_month:[],
      reportActivity:[],
      activityAllfilter:[],
      filter:false,
      barchart:[],
      radarchart:{radarchart:[{hashtag:'',value:''}],label:[]},
      chartLoading:true,
      listChartmonth:[],
      radarchartAll:[],
      participation:[],
      medal:[],
      dataOverAll:{          
        totalRecognition:0,
        pasttotalRecognition:0,
        chartRecognition:[],
        compareRecognition:'',
        value_aligned:0,
        pastvalue_aligned:0,
        comparevalue_aligned:'',
        percent_value_aligned:'',
        comparepercent_value_aligned:'',

        participation:''
        },
      data_all_day:[],
      data_all_day_participation:[],
      herepoint_cache:[] as Array<any>,
      herepoint_cache_company:'',
    },
    mutations: {
      setActivity(state:any, result:any) {
        
        state.activity = result
      },
      setActivityall(state:any, result:any) {
        state.activityall = result
      },
      setPage(state:any,result:any) {
        state.page = result
      },
      setResults(state:any, result:any) {
        state.results = result
      },
      setMedal(state:any, result:any) {
        state.medal = result
      },
      setList_month(state:any, result:any) {
        state.list_month = result
      },
      setActivityReport(state:any, result:any) {
        state.reportActivity = result
      },
      setActivityAllfilter(state:any, result:any) {
        state.activityAllfilter = result
      },
      setFilter(state:any, result:any) {
        state.filter = result
      },
      setBarchart(state:any, result:any) {
        
        state.barchart = result
      },
      setChartLoading(state:any, result:any) {
        state.chartLoading = result
      },
      setRadarchart(state:any, result:any) {
        state.radarchart = result
      },
      setRadarchartAll(state:any, result:any) {
        state.radarchartAll = result
      },
      setParticipation(state:any, result:any) {
        state.participation = result
      },
      setListChartmonth(state:any, result:any) {
        state.listChartmonth = result
      },
      setDataOverAll(state:any, result:any) {
        state.dataOverAll = result
      },
      setdata_all_day(state:any, result:any) {
        state.data_all_day = result
      },
      setdata_all_day_participation(state:any, result:any) {
        state.data_all_day_participation = result
      },
      setHerepointCache(state:any, result:any) {
        state.herepoint_cache = result
      },
      setHerepointCacheCompany(state:any, result:any) {
        state.herepoint_cache_company = result
      }
    },
    actions: {
      async getData({commit,rootState,dispatch,state}:any, value:any){
        const arrData:Array<any> = [];
        const { startAt, endAt } = resolveDateRange(value)
        const scopeKey = value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        let classrooms_gift:Array<any> = [];
        const medal=[
        { type: "+3 coins" },
        { type: "+5 coins" },
        { type: "+1 gems" },]
        const company = await getCompany(rootState.authen.user.company)//(await firebase.database().ref(`company/${rootState.authen.user.company}`).once('value')).val()
        
        if(company.medal){
          for(const con in company.medal){
            medal.push({type:company.medal[con]})
          }
        }
        medal.push(
          { type: "All coins" },
          { type: "All gems" },)
        await firebase.firestore().collection('classrooms_gift').where("company", "==", value.user.company).get()
        .then((querySnapshot)=> {
          querySnapshot.forEach((doc)=> {
            const u = doc.data()
            classrooms_gift.push(u)
          })
        })
        await firebase.firestore().collection('here_point').where('company','==',value.user.company).where('date','>',startAt).where('date','<=',endAt).get()
        .then((querySnapshot)=> {
            querySnapshot.forEach((doc)=> {
              const u = doc.data()
              u.id = doc.id
              let from=u.send_anony_name? u.send_anony_name.split(" "):value.user.company_name
              const lastname=from[1]?from[1].slice(0, 1):""
              u.from=u.send_anony_name?from[0]+" "+lastname:value.user.company_name
              if(u.date){
                u.send_on = format(u.date, 'MMM D, YYYY') +" at "+ format(u.date, 'h:mmA')
              }
              let core_value_ai_value:Array<any>=[]

              if(u.core_value_ai&&u.core_value_ai.length>0){
                for(const i in u.core_value_ai){
                  if(u.core_value_ai[i].rating>4){
                    core_value_ai_value.push(u.core_value_ai[i]) 
                  }

                }
                u.core_value_ai=core_value_ai_value
              }

              

              //send to
              if(u.send_to_name=="Everyone")
              {
                u.send_to="All Members"
              }else if(_.size(u.profileimage)>1){
                u.send_to = "Multiple (" +_.size(u.profileimage) + ")"
              }else if(u.send_to_name=="Avenger"){
                u.send_to="Avenger"
              } else {
                let send=u.send_to_name? u.send_to_name.split(" "):""
                u.send_to=u.send_to_name?send[0]+" "+send[1].slice(0, 1):""
              }


              //type
              if (u.type === 'coin' && u.medal != undefined){
                 var reward=""
                u.medal.map((value,i)=>{
                  if(i==0)
                  {
                    reward=value
                  }else{reward=reward+","+value}
                })
                u.reward = reward
                u.rewardSorting='1'
                arrData.push(u)

              }else if(u.type== 'classroom gift'){
                var gift=_.filter(
                  classrooms_gift,
                  function (currentObject) {
                      return currentObject.class_id === u.class_id2;
                  }
              );
                if(_.size(gift)>1){
                  var count=0
                  gift.map((value)=>{
                    count=count+_.size(value.members)
                  })
                  u.send_to = "Multiple (" +count+ ")"
                }
                else{ u.send_to =""}
                u.from=value.user.company_name
                u.reason=`Attending ${ u.reason } in the Happily University`
                if(u.point > 1){
                  u.unit = "coins"
                }else{
                  u.unit = "coin"
                }
                u.rewardSorting='3_'+u.point
                arrData.push(u)
              }else if(u.type== 'gem'&&u.category==undefined){
                    if(u.point > 1){
                      u.unit = "gems"
                    }else{
                      u.unit = "gem"
                    }
                    u.rewardSorting='2_'+u.point
                    arrData.push(u)

              }else if(u.type=='powerup'){

                u.from=value.user.company_name
                if(u.type2=='coin')
                {
                    if(u.point > 1){
                      u.unit = "coins"
                    }else{
                      u.unit = "coin"
                    }
                    u.rewardSorting='3_'+u.point
                }else if(u.type2=='gem'){
                  if(u.point > 1){
                    u.unit = "gems"
                  }else{
                    u.unit = "gem"
                  }
                  u.rewardSorting='2_'+u.point
                }else if(u.type2=="medal"){
                  var reward=""
                  // u.medal.map((value,i)=>{
                  //   if(i==0)
                  //   {
                  //     reward=value
                  //   }else{reward=reward+","+value}
                  // })
                  u.reward = u.medals&&u.medals[u.medal]?u.medals[u.medal].en:u.medal
                  u.rewardSorting='1'
                }
                
                arrData.push(u)
              }else if(u.type=='coin'&&u.category==undefined){
                
                    if(u.point > 1){
                      u.unit = "coins"
                    }else{
                      u.unit = "coin"
                    }
                u.rewardSorting='3_'+u.point
                arrData.push(u)
              }
            });
        });
        const scopedData = filterRecordsByScope(arrData, allowedEmailSet)
        commit('setMedal',medal)
        commit('setResults',scopedData.length)
        commit('setActivityall', scopedData)
        commit('setActivity', scopedData.slice(0, state.size_page))
        // if(value.hashtag.length>0){
        //   dispatch("getDataHashtag",value.hashtag)
        // }
      },
      currentChange({commit,state}:any,page:any){
        var size = state.size_page
        var start_row = (page-1) * size
        var end_row = ((page-1) * size)+size
 
       
        let data: Array<any> = [];
        if(state.filter==true){
          data.push(state.activityAllfilter)
        }else{
          data.push(state.activityall)
        }

        commit('setPage',page)
        commit('setActivity',data[0].slice(start_row,end_row))
      },
      deletePowerup({commit,state,rootState,dispatch}:any,value:any){
        if(value.id){
          firebase.firestore().collection('powerups').doc(value.id).delete().then(function() {
            dispatch('getData',rootState.authen.user)
          });
        }
      },
      getList_month({ commit, dispatch, state }: any, user: any) {
        const arr_list_month: Array<any> = [];
        const arr_chart_month: Array<any> = [];
        var dateNow = new Date()
        for(let i=0;i<=11;i++){
          arr_list_month.push({'value':format(addMonths(dateNow, -i), 'YYYY-MM'),'name':format(addMonths(dateNow, -i), 'MMMM YYYY'),'id':format(addMonths(dateNow, -i), 'MMMM YYYY')})
        }
        for(let i=11;i>=0;i--){
          arr_chart_month.push(format(subMonths(new Date(), i), 'MMM YYYY'))
        }
        commit('setList_month', arr_list_month)
        commit('setListChartmonth', arr_chart_month)
      },
      async getDataReport({commit,rootState,dispatch,state}:any, value:any){
        const arrData:Array<any> = [];
        const { startAt, endAt } = resolveDateRange(value)
        const scopeKey = value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        let classrooms_gift:Array<any> = [];
        await firebase.firestore().collection('classrooms_gift').where("company", "==", value.user.company).get()
        .then((querySnapshot)=> {
          querySnapshot.forEach((doc)=> {
            const u = doc.data()
            classrooms_gift.push(u)
          })
        })
        await firebase.firestore().collection('here_point').where('company','==',value.user.company).where('date','>',startAt).where('date','<=',endAt).get()
        .then((querySnapshot)=> {
            querySnapshot.forEach((doc)=> {
              const u = doc.data()
              u.id = doc.id
              let core_value_ai_value:Array<any>=[]

              if(u.core_value_ai&&u.core_value_ai.length>0){
               
                for(const i in u.core_value_ai){
                  if(u.core_value_ai[i].rating>4){
                    core_value_ai_value.push(u.core_value_ai[i]) 
                    
                  }

                }
                u.core_value_ai=core_value_ai_value
              }
              let from=u.send_anony_name? u.send_anony_name.split(" "):value.user.company_name
              const lastname=from[1]?from[1].slice(0, 1):""
              u.from=u.send_anony_name?from[0]+" "+lastname:value.user.company_name
              if(u.date){
                u.send_on = format(u.date, 'MMM D, YYYY') +" at "+ format(u.date, 'h:mmA')
              }

              

              //send to
              if(u.send_to_name=="Everyone")
              {
                u.send_to="All Members"
              }else if(_.size(u.profileimage)>1){
                u.send_to = "Multiple (" +_.size(u.profileimage) + ")"
              }else if(u.send_to_name=="Avenger"){
                u.send_to="Avenger"
              } else {
                let send=u.send_to_name? u.send_to_name.split(" "):""
                u.send_to=u.send_to_name?send[0]+" "+send[1].slice(0, 1):""
              
              }
              


              //type
              if (u.type === 'coin' && u.medal != undefined){
                //le.log("medal",u)
                 var reward=""
                  u.medal.map((value,i)=>{
                  if(i==0)
                  {
                    reward=value
                  }else{reward=reward+","+value}
                })
                u.reward = reward
                arrData.push(u)

              }else if(u.type== 'classroom gift'){
                
                var gift=_.filter(
                  classrooms_gift,
                  function (currentObject) {
                      return currentObject.class_id === u.class_id2;
                  }
              );
             
                if(_.size(gift)>1){
                  var count=0
                  var email=""
                  gift.map((value)=>{
                    count=count+_.size(value.members)
                    value.members.map((member)=>{
                      if(email=="")
                      {
                        email=email+member.email
                      }else{email=email+","+member.email}
                      
                    })
                    
                  })
                  u.send_to = "Multiple (" +count+ ")"
                  u.send_to_email=email
                }
                else{ u.send_to =""}
                u.from=value.user.company_name
                u.reason=`Attending ${ u.reason } in the Happily University`
                if(u.point > 1){
                  u.unit = "coins"
                }else{
                  u.unit = "coin"
                }
                arrData.push(u)
              }else if(u.type== 'gem'&&u.category==undefined){
                  // console.log("gem",u)
                    if(u.point > 1){
                      u.unit = "gems"
                    }else{
                      u.unit = "gem"
                    }
                    arrData.push(u)

              }else if(u.type=='powerup'){
                // console.log("powerup",u)
                u.from=value.user.company_name
                if(u.type2=='coin')
                {
                    if(u.point > 1){
                      u.unit = "coins"
                    }else{
                      u.unit = "coin"
                    }
                }else if(u.type2=='gem'){
                  if(u.point > 1){
                    u.unit = "gems"
                  }else{
                    u.unit = "gem"
                  }
                }else if(u.type2=="medal"){
                  
                  var reward=""
                  if(typeof u.medal=="string"){
                    reward=u.medal
                  }else{
                  u.medal.map((value,i)=>{
                    if(i==0)
                    {
                      reward=value
                    }else{reward=reward+","+value}
                  })
                }
                  u.reward = reward
                }
                arrData.push(u)
              }else if(u.type=='coin'&&u.category==undefined){
                //console.log("coin",u)
                    if(u.point > 1){
                      u.unit = "coins"
                    }else{
                      u.unit = "coin"
                    }
                arrData.push(u)
              }
            });
        });
        const scopedData = filterRecordsByScope(arrData, allowedEmailSet)
        if(state.filter==true){
         const activityall=scopedData
         let arrData1:Array<any> = [];
          let _arrData:Array<any> = [];

          let countdata=0
          //console.log("value",value)
          if(value.core_value.length>0){
            countdata=countdata+1
            activityall.map(v=>{
              if(v.hashtag){
                value.core_value.map(data=>{
                  const activity= _.filter(v.hashtags, { name:data}); 
  
                  if(activity.length>0){
                    arrData1.push(v)
                  }
                })
              }
  
            })
            if(arrData1.length>0){
              arrData1 =_.unionBy(arrData1, 'id');
            }
  
        }
        if(value.classification.length>0){
          countdata=countdata+1
          activityall.map(v=>{
            if(v.effort>=100){
              arrData1.push(v)
              
            }
          })
        }
  
        if(value.reward.length>0){
  
          let arr_Data:Array<any>=[]
          countdata=countdata+1
         activityall.map(u=>{
            value.reward.map(data=>{
                //type
                if (u.type === 'coin' && u.medal != undefined){
                   const filtermedal=_.filter(u.medal, function (val) {  
                    return val === data;
                   })
                   if(filtermedal.length>0){
                    arr_Data.push(u)
                   }
  
                }else if(u.type== 'classroom gift'){
                 // console.log("classroom gift",u)
                 if(data=='All coins'){
                  arr_Data.push(u)
                 }
                 else if(data=='+3 coins'&&u.point==3){
                  arr_Data.push(u)
                 }else if(data=='+5 coins'&&u.point==5){arr_Data.push(u)} 
                }
                else if(u.type== 'gem'&&u.category==undefined){
                  if(data=='All gems'){
                    arr_Data.push(u)
                   }
                   else if(data=='+1 gems'&&u.point==1){
                    arr_Data.push(u)
                   }
                }
                else if(u.type=='powerup'){
  
                  // console.log("powerup",u)
                  if(u.type2=='coin')
                  {
                    if(data=='All coins'){arr_Data.push(u)}
                    else if(data=='+3 coins'&&u.point==3){arr_Data.push(u)}
                    else if(data=='+5 coins'&&u.point==5){arr_Data.push(u)} 
                  }else if(u.type2=='gem'){
                    if(data=='All gems'){
                      arr_Data.push(u)
                     }
                     else if(data=='+1 gems'&&u.point==1){
                      arr_Data.push(u)
                     }
                  }else if(u.type2=="medal"){
                    const filtermedal=_.filter(u.medal, function (val) {  
                      return val === data;
                     })
                     if(filtermedal.length>0){
                      arr_Data.push(u)
                     }
                  }
                  
                }
                else if(u.type=='coin'&&u.category==undefined){
                  if(data=='All coins'){
                    arr_Data.push(u)
                   }
                   else if(data=='+3 coins'&&u.point==3){
                    arr_Data.push(u)
                   }else if(data=='+5 coins'&&u.point==5){arr_Data.push(u)} 
                 }
  
            })
          })
          if(arr_Data.length>0){
            arr_Data =_.unionBy(arr_Data, 'id');
            arrData1.push(...arr_Data)
          }
  
        }

        let arr = _.groupBy(arrData1, function (b) {
          return b.id;
        });
        for(let key in arr){
          if(arr[key].length>=countdata){
            _arrData.push(arr[key][0])
          }
        }
          commit('setActivityReport', _arrData)
        }
        // if(value.hashtag.length>0){

        //   arrData.map(v=>{
        //     if(v.hashtag){
        //       value.hashtag.map(data=>{
        //         const activity= _.filter(v.hashtags, { name:data}); 
  
        //         if(activity.length>0){
        //           fiterArrData.push(v)
        //         }
        //       })
        //     }
  
        //   })
        //   if(fiterArrData.length>0){
        //     fiterArrData =_.unionBy(fiterArrData, 'id');
        //   }

        //   commit('setActivityReport', fiterArrData)
        // }
        else {
          commit('setActivityReport', scopedData)
        }
        
        
      },
      getdatafilter({commit,rootState,dispatch,state}:any, value:any){
        commit('setFilter',true)
        let arrData:Array<any> = [];
        let _arrData:Array<any> = [];
        var start_row = (state.page-1) * state.size_page
        var end_row = ((state.page-1) * state.size_page)+state.size_page
        let countdata=0
        //console.log("value",value)
        if(value.core_value.length>0){
          countdata=countdata+1
          state.activityall.map(v=>{
            if(v.hashtag){
              value.core_value.map(data=>{
                const activity= _.filter(v.hashtags, { name:data}); 

                if(activity.length>0){
                  arrData.push(v)
                }
              })
            }

          })
          if(arrData.length>0){
            arrData =_.unionBy(arrData, 'id');
          }

      }
      if(value.classification.length>0){
        countdata=countdata+1
        state.activityall.map(v=>{
          if(v.effort>=100){
            arrData.push(v)
          }
        })
      }

      if(value.reward.length>0){

        let arr_Data:Array<any>=[]
        countdata=countdata+1
        state.activityall.map(u=>{
          value.reward.map(data=>{
              //type
              if (u.type === 'coin' && u.medal != undefined){
                 const filtermedal=_.filter(u.medal, function (val) {  
                  return val === data;
                 })
                 if(filtermedal.length>0){
                  arr_Data.push(u)
                 }

              }else if(u.type== 'classroom gift'){
               // console.log("classroom gift",u)
               if(data=='All coins'){
                arr_Data.push(u)
               }
               else if(data=='+3 coins'&&u.point==3){
                arr_Data.push(u)
               }else if(data=='+5 coins'&&u.point==5){arr_Data.push(u)} 
              }
              else if(u.type== 'gem'&&u.category==undefined){
                if(data=='All gems'){
                  arr_Data.push(u)
                 }
                 else if(data=='+1 gems'&&u.point==1){
                  arr_Data.push(u)
                 }
              }
              else if(u.type=='powerup'){

                // console.log("powerup",u)
                if(u.type2=='coin')
                {
                  if(data=='All coins'){arr_Data.push(u)}
                  else if(data=='+3 coins'&&u.point==3){arr_Data.push(u)}
                  else if(data=='+5 coins'&&u.point==5){arr_Data.push(u)} 
                }else if(u.type2=='gem'){
                  if(data=='All gems'){
                    arr_Data.push(u)
                   }
                   else if(data=='+1 gems'&&u.point==1){
                    arr_Data.push(u)
                   }
                }else if(u.type2=="medal"){
                  const filtermedal=_.filter(u.medal, function (val) {  
                    return val === data;
                   })
                   if(filtermedal.length>0){
                    arr_Data.push(u)
                   }
                }
                
              }
              else if(u.type=='coin'&&u.category==undefined){
                if(data=='All coins'){
                  arr_Data.push(u)
                 }
                 else if(data=='+3 coins'&&u.point==3){
                  arr_Data.push(u)
                 }else if(data=='+5 coins'&&u.point==5){arr_Data.push(u)} 
               }

          })
        })
        if(arr_Data.length>0){
          arr_Data =_.unionBy(arr_Data, 'id');
          arrData.push(...arr_Data)
        }

      }
      if(value.reward.length==0&&value.classification.length==0&&value.core_value.length==0){
       commit('setFilter',false)
        _arrData=state.activityall
      }
      let arr = _.groupBy(arrData, function (b) {
        return b.id;
      });
      for(let key in arr){
        if(arr[key].length>=countdata){
          _arrData.push(arr[key][0])
        }
      }
        commit('setResults',_arrData.length)
        commit('setActivity', _arrData.slice(start_row,end_row))
        commit('setActivityAllfilter', _arrData)
      },
      sortData({commit,state}:any,val:any){
        var size = state.size_page
        var start_row = (state.page-1) * size
        var end_row = ((state.page-1) * size)+size
        let data: Array<any> = [];
        const order=val.order=='ascending'? 'asc':'desc'
        if(state.filter==true){
          if(val.column=='date'){
            data=_.orderBy(state.activityAllfilter, function(dateObj) {
              return new Date(dateObj.date);
            },[order]);
          }else{
            data=_.orderBy(state.activityAllfilter, [val.column],[order]);
          }
          
          commit('setActivityAllfilter', data)
        }else{
          if(val.column=='date'){
            data=_.orderBy(state.activityall, function(dateObj) {
  
              return new Date(dateObj.date);
            },[order]);
      
          }else{
            data=_.orderBy(state.activityall, [val.column], [order]);
          }
          commit('setActivityall', data)
        }
        commit('setActivity',data.slice(start_row,end_row)) 
       
      },
      async fetchHerepointChunked({commit, state}:any, company:string) {
        if (state.herepoint_cache.length > 0 && state.herepoint_cache_company === company) {
          return state.herepoint_cache
        }
        const allRecords:Array<any> = []
        const now = new Date()
        const chunks:Array<{start:string, end:string}> = []
        for (var mi = 11; mi >= 0; mi--) {
          var chunkStart = format(subMonths(now, mi), 'YYYY-MM') + '-01 00:00'
          var chunkEnd = mi === 0
            ? format(now, 'YYYY-MM-DD') + ' 23:59'
            : format(subMonths(now, mi), 'YYYY-MM') + '-31 23:59'
          chunks.push({ start: chunkStart, end: chunkEnd })
        }

        var fetchPromises:Array<any> = []
        for (var ci = 0; ci < chunks.length; ci++) {
          var chunk = chunks[ci]
          fetchPromises.push(
            firebase.firestore().collection('here_point')
              .where('company', '==', company)
              .where('statusrow', 'in', ['sendcoin'])
              .where('date', '>=', chunk.start)
              .where('date', '<=', chunk.end)
              .get()
          )
        }

        var snapshots = await Promise.all(fetchPromises)
        for (var si = 0; si < snapshots.length; si++) {
          snapshots[si].forEach(function(doc:any) {
            var d = doc.data()
            d.id = doc.id
            allRecords.push(d)
          })
        }

        commit('setHerepointCache', allRecords)
        commit('setHerepointCacheCompany', company)
        return allRecords
      },
      async getChart({commit,rootState,dispatch,state}:any, value:any){
        commit('setChartLoading',true) 
        const scopeKey = value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS
        const scopeType = getScopeType(scopeKey, rootState)
        const isAllScope = scopeType === 'all'
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        const arrData = {};
        const obj: { [key: string]: string} = {};
        const hashtag :Array<any> = [];
        const barchart:Array<any> = [];
        const radarchart= {};
        const hashtagBarchart:Array<any> = [];
        const data_all_day:Array<any> = [];
        let data_day:{ [key: string]: any} = {};
        let current = format(new Date(), 'YYYY-MM')
        const company = await getCompany(value.company)
        if(company.hashtag){
          for(let data in company.hashtag){
            let hashtags=company.hashtag[data].name.replace("#","")
            let str=hashtags.toString()
            hashtag.push(str)
            Object.assign(obj,{[str]:0});
          }
         
        }

        if(isAllScope){
          await firebase.firestore().collection('recognition_month').where('company','==',value.company).get().then((querySnapshot)=> {
            querySnapshot.forEach((doc)=> {
              const u = doc.data()
              arrData[u.date]={
                activity:0,
                hashtag_value:0,
                hashtag:{}}
              if(u.arr_Data){
                for(const date in u.arr_Data){
                  data_all_day.push({...u.arr_Data[date],date:date})
                  const d=format(new Date(date), 'YYYY-MM')
                    arrData[d].activity=arrData[d].activity+u.arr_Data[date].activity
                    arrData[d].hashtag_value=arrData[d].hashtag_value+u.arr_Data[date].hashtag_value
                    if(u.arr_Data[date].hashtag){
                      for(const i in u.arr_Data[date].hashtag){
                        if(!arrData[d].hashtag[i]){
                          arrData[d].hashtag[i]=0
                        }
                        arrData[d].hashtag[i]=arrData[d].hashtag[i]+u.arr_Data[date].hashtag[i]
                      }
                    }
                }
              }
            })
          })
        }

      const herepointRaw:Array<any> = await dispatch('fetchHerepointChunked', value.company)
      const herepoint = filterRecordsByScope(herepointRaw, allowedEmailSet)
        // const herepoint=await firebase.firestore().collection('here_point').where('company','==',value.company).where('date','>=','2025-11-01 00:00').where('date','<=','2025-10-31 23:59').where('statusrow','in',['sendcoin']).get().then((querySnapshot)=> {
        //   return querySnapshot.docs.map(doc=>doc.data())
        // })
        //console.log('herepoint',herepoint)
        for(const i in herepoint){
          const u:any = herepoint[i]
          u.id =i
          
          if(u.statusrow){
            if(!data_day[format(u.date, 'YYYY-MM-DD')]){
              data_day[format(u.date, 'YYYY-MM-DD')]={
                activity:0,
                hashtag_value:0,
                hashtag:{},
                date:format(u.date, 'YYYY-MM-DD')}
              }
            
            data_day[format(u.date, 'YYYY-MM-DD')].activity++
            let date=format(u.date, 'YYYY-MM')
            if(!arrData[date]){
              arrData[date]={
                ...obj,
                activity:0,
                hashtag_value:0
              }
            }
            arrData[date].activity++
            if(u.hashtag&&u.hashtag.length>0){
              data_day[format(u.date, 'YYYY-MM-DD')].hashtag_value++
              arrData[date].hashtag_value++
              u.hashtag.map(v=>{
                let hashtags=v.replace("#","")
                if(!data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]){
                  data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]=0
                }
                data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]++
                arrData[date][hashtags]++
              })
            }else{
              if(u.hashtag_prev&&u.hashtag_prev.length>0){
                //console.log('u.hashtag_prev',u.hashtag_prev)
              }
            }
          }
        }
        for(var i=11;i>=0;i--){
          const radar:Array<any> = [];
          let d= format(subMonths(new Date(), i), 'YYYY-MM')
          if(arrData[d]!==undefined){
            hashtagBarchart.push(arrData[d].hashtag_value)
            barchart.push(arrData[d].activity)
            if(arrData[d].hashtag){
              
              for(const i in arrData[d].hashtag){
              //  radar.push(i)
                radar.push({hashtag:i,value:arrData[d].hashtag[i]})
              }
              
          
            }
            else{

              hashtag.map(data=>{
                
                // console.log('arrData[d][data]',arrData[d][data])
                 //radar.push(arrData[d][data])
                radar.push({hashtag:data,value:arrData[d][data]})
              })
            }
      
       
            radarchart[d]=radar
          }else{
           let _data ={
              activity: 0,
              alwaysdatadriven: 0,
              bepunctual: 0,
              brave: 0,
              empathy: 0,
              focus: 0,
              hashtag_value: 0,
              keepinsync: 0,
              opencommunication: 0,
              perfectdetails: 0,
              problemsolver: 0,
              teamwork: 0,
              usersrule: 0,
              }
            hashtagBarchart.push(_data.hashtag_value)
            barchart.push(_data.activity)
            hashtag.map(data=>{
               radar.push({hashtag:data,value:_data[data]})
              // radar.push(_data[data])
            })
            radarchart[d]=radar
          }             
        }

        for(const i in data_day){
          data_all_day.push(data_day[i])
        }
        commit('setdata_all_day',data_all_day)
        commit('setBarchart',{barchart:barchart,hashtagBarchart:hashtagBarchart,participation:state.participation})  
        commit('setRadarchart',{radarchart:radarchart[current] || [],label:hashtag}) 
        commit('setRadarchartAll',radarchart) 
        commit('setChartLoading',false) 


        // await firebase.firestore().collection('here_point').where('company','==',value.company).where('statusrow','in',['sendcoin']).where('date','>=',startAt).get()
        // .then((querySnapshot)=> {
        //     querySnapshot.forEach((doc)=> {
        //       const u = doc.data()
        //       u.id = doc.id
        //       if(doc.data().statusrow){
        //         if(!data_day[format(u.date, 'YYYY-MM-DD')]){
        //           data_day[format(u.date, 'YYYY-MM-DD')]={
        //             activity:0,
        //             hashtag_value:0,
        //             hashtag:{},
        //             date:format(u.date, 'YYYY-MM-DD')}
        //           }
                
        //         data_day[format(u.date, 'YYYY-MM-DD')].activity++
        //         let date=format(u.date, 'YYYY-MM')
        //         if(!arrData[date]){
        //           arrData[date]={
        //             ...obj,
        //             activity:0,
        //             hashtag_value:0
        //           }
        //         }
        //         arrData[date].activity++
        //         if(u.hashtag&&u.hashtag.length>0){
        //           data_day[format(u.date, 'YYYY-MM-DD')].hashtag_value++
        //           arrData[date].hashtag_value++
        //           u.hashtag.map(v=>{
        //             let hashtags=v.replace("#","")
        //             if(!data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]){
        //               data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]=0
        //             }
        //             data_day[format(u.date, 'YYYY-MM-DD')].hashtag[hashtags]++
        //             arrData[date][hashtags]++
        //           })
        //         }
        //       }
        //     });
        //     for(var i=11;i>=0;i--){
        //       const radar:Array<any> = [];
        //       let d= format(subMonths(new Date(), i), 'YYYY-MM')
        //       if(arrData[d]!==undefined){
        //         hashtagBarchart.push(arrData[d].hashtag_value)
        //         barchart.push(arrData[d].activity)
        //         if(arrData[d].hashtag){
                  
        //           for(const i in arrData[d].hashtag){
        //           //  radar.push(i)
        //             radar.push({hashtag:i,value:arrData[d].hashtag[i]})
        //           }
                  
              
        //         }
        //         else{

        //           hashtag.map(data=>{
                    
        //             // console.log('arrData[d][data]',arrData[d][data])
        //              //radar.push(arrData[d][data])
        //             radar.push({hashtag:data,value:arrData[d][data]})
        //           })
        //         }
          
           
        //         radarchart[d]=radar
        //       }else{
        //        let _data ={
        //           activity: 0,
        //           alwaysdatadriven: 0,
        //           bepunctual: 0,
        //           brave: 0,
        //           empathy: 0,
        //           focus: 0,
        //           hashtag_value: 0,
        //           keepinsync: 0,
        //           opencommunication: 0,
        //           perfectdetails: 0,
        //           problemsolver: 0,
        //           teamwork: 0,
        //           usersrule: 0,
        //           }
        //         hashtagBarchart.push(_data.hashtag_value)
        //         barchart.push(_data.activity)
        //         hashtag.map(data=>{
        //            radar.push({hashtag:data,value:_data[data]})
        //           // radar.push(_data[data])
        //         })
        //         radarchart[d]=radar
        //       }             
        //     }

        //     for(const i in data_day){
        //       data_all_day.push(data_day[i])
        //     }
        //     commit('setdata_all_day',data_all_day)
           
        //     commit('setBarchart',{barchart:barchart,hashtagBarchart:hashtagBarchart,participation:state.participation})  
        //     commit('setRadarchart',{radarchart:radarchart[current],label:hashtag}) 
        //     commit('setRadarchartAll',radarchart) 
        //     dispatch('getOverAll','Past 30 days')
            
           
        // });

      },
      async recomputeChartFromCache({commit,rootState,dispatch,state}:any, value:any){
        commit('setChartLoading',true) 
        const scopeKey = value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        const arrData:any = {};
        const obj: { [key: string]: string} = {};
        const hashtag :Array<any> = [];
        const barchart:Array<any> = [];
        const radarchart:any = {};
        const hashtagBarchart:Array<any> = [];
        const data_all_day:Array<any> = [];
        var data_day:{ [key: string]: any} = {};
        var current = format(new Date(), 'YYYY-MM')
        const company = await getCompany(value.company)
        if(company.hashtag){
          for(var data in company.hashtag){
            var hashtags=company.hashtag[data].name.replace("#","")
            var str=hashtags.toString()
            hashtag.push(str)
            Object.assign(obj,{[str]:0});
          }
        }

        var herepointRaw = state.herepoint_cache || []
        var herepoint = filterRecordsByScope(herepointRaw, allowedEmailSet)

        for(var hi = 0; hi < herepoint.length; hi++){
          var u:any = herepoint[hi]
          if(u.statusrow){
            var dayKey = format(u.date, 'YYYY-MM-DD')
            if(!data_day[dayKey]){
              data_day[dayKey]={
                activity:0,
                hashtag_value:0,
                hashtag:{},
                date:dayKey}
            }
            data_day[dayKey].activity++
            var monthKey = format(u.date, 'YYYY-MM')
            if(!arrData[monthKey]){
              arrData[monthKey]={
                ...obj,
                activity:0,
                hashtag_value:0,
                hashtag:{}
              }
            }
            arrData[monthKey].activity++
            if(u.hashtag&&u.hashtag.length>0){
              data_day[dayKey].hashtag_value++
              arrData[monthKey].hashtag_value++
              u.hashtag.map(function(v:any){
                var ht=v.replace("#","")
                if(!data_day[dayKey].hashtag[ht]){
                  data_day[dayKey].hashtag[ht]=0
                }
                data_day[dayKey].hashtag[ht]++
                if(!arrData[monthKey].hashtag){
                  arrData[monthKey].hashtag={}
                }
                if(!arrData[monthKey].hashtag[ht]){
                  arrData[monthKey].hashtag[ht]=0
                }
                arrData[monthKey].hashtag[ht]++
                if(arrData[monthKey][ht] === undefined){
                  arrData[monthKey][ht]=0
                }
                arrData[monthKey][ht]++
              })
            }
          }
        }

        for(var mi=11;mi>=0;mi--){
          var radar:Array<any> = [];
          var d= format(subMonths(new Date(), mi), 'YYYY-MM')
          if(arrData[d]!==undefined){
            hashtagBarchart.push(arrData[d].hashtag_value)
            barchart.push(arrData[d].activity)
            if(arrData[d].hashtag){
              for(var hk in arrData[d].hashtag){
                radar.push({hashtag:hk,value:arrData[d].hashtag[hk]})
              }
            } else {
              hashtag.map(function(htd:any){
                radar.push({hashtag:htd,value:arrData[d][htd]})
              })
            }
            radarchart[d]=radar
          } else {
            hashtagBarchart.push(0)
            barchart.push(0)
            hashtag.map(function(htd:any){
              radar.push({hashtag:htd,value:0})
            })
            radarchart[d]=radar
          }
        }

        for(var dk in data_day){
          data_all_day.push(data_day[dk])
        }

        commit('setdata_all_day',data_all_day)
        commit('setBarchart',{barchart:barchart,hashtagBarchart:hashtagBarchart,participation:state.participation})
        commit('setRadarchart',{radarchart:radarchart[current] || [],label:hashtag})
        commit('setRadarchartAll',radarchart)
        commit('setChartLoading',false) 
      },
      changeRedarChart({commit,rootState,dispatch,state}:any, value:any){
        let monthSelect = format(value.selectedMonth, 'YYYY-MM')
        commit('setRadarchart',{radarchart:state.radarchartAll[monthSelect],label:value.label}) 
      },
      aggregateHeatmapForPeriod({commit,state}:any, value:any){
        const { startDate, endDate } = value;
        const aggregated: { [key: string]: number } = {};
        
        // Iterate through all months in the period
        const start = new Date(startDate);
        const end = new Date(endDate);
        let current = new Date(start.getFullYear(), start.getMonth(), 1);
        
        while(current <= end){
          const monthKey = format(current, 'YYYY-MM');
          const monthData = state.radarchartAll[monthKey];
          
          if(monthData && Array.isArray(monthData)){
            monthData.forEach((item: any) => {
              if(item && item.hashtag){
                if(!aggregated[item.hashtag]){
                  aggregated[item.hashtag] = 0;
                }
                aggregated[item.hashtag] += item.value || 0;
              }
            });
          }
          
          current.setMonth(current.getMonth() + 1);
        }
        
        // Convert to array format and sort by value
        const aggregatedRadarchart = Object.entries(aggregated).map(([hashtag, value]) => ({
          hashtag,
          value
        })).sort((a, b) => b.value - a.value);
        
        if(aggregatedRadarchart.length > 0){
          commit('setRadarchart', {
            radarchart: aggregatedRadarchart,
            label: Object.keys(aggregated)
          });
        }
      },
      async getRecognition({commit,rootState,dispatch,state}:any, value:any){
        commit('setChartLoading',true) 
        const scopeKey = value && value.scopeKey ? value.scopeKey : ALL_TEAMS_AND_COHORTS
        const allowedEmailSet = buildAllowedEmailSet(scopeKey, rootState)
        let activeUser:any={}
        let activeUserAll:any={}
        let givenAll:any={}
        let given:any={}
        let currentDate=format(new Date(),"YYYY-MM")
        let month12ago=format(subMonths(new Date(), 11), 'YYYY-MM')
        let years2=format(subDays(new Date(), 730), 'YYYY-MM')
        let dateAll:Array<any>=[]
        let rate:Array<any>=[]
        for(let i=11;i>=0;i--){
          let d=format(subMonths(new Date(), i), 'YYYY-MM')
          dateAll.push(d)
          if(!activeUser[d]){
            activeUser[d]=[]
          }
          if(!given[d]){
            given[d]=[]
          }
        }
        firebase.firestore().collection('summary_active_users').where("company", "==", rootState.authen.user.company).where('date', '>=', years2).get()
        .then((querySnapshot)=> {
        querySnapshot.forEach((doc)=> {
          let data=doc.data()
          let date =format(data.date,"YYYY-MM")
          if(data.users){
            const scopedUsers = filterUsersByScope(data.users, allowedEmailSet)
            if(!activeUserAll[data.date]){
              activeUserAll[data.date]=[]
            }
            activeUserAll[data.date].push({date:data.date,users:scopedUsers})
            if(date>=month12ago&&date<=currentDate)
              {
                activeUser[date].push(...scopedUsers)
              }
          }
          
        })
        if(activeUser ){
          dateAll.map(data=>{
            activeUser[data]=_.groupBy(activeUser[data], function (b) { return b })
          })
        }
        firebase.firestore().collection('summary_send_coins').where("company", "==", rootState.authen.user.company).where('date', '>=', years2).get()
        .then((querySnapshot)=> {
        querySnapshot.forEach((doc)=> {
            let data=doc.data()
            let date =format(data.date,"YYYY-MM")
            const scopedUsers = filterUsersByScope(data.users, allowedEmailSet)
            if(!givenAll[data.date]){
              givenAll[data.date]=[]
            }
            givenAll[data.date].push({date:data.date,users:scopedUsers})
                if(date>=month12ago&&date<=currentDate)
                {
                  given[date].push(...scopedUsers)
                }
                
          })
            if(given){
              dateAll.map(data=>{
              
                given[data]=_.groupBy(given[data], function (b) { return b })
                rate.push( (Object.keys(given[data]).length*100/ Object.keys(activeUser[data]).length).toFixed(2))
              })
              commit('setdata_all_day_participation',{givenAll:givenAll,activeUserAll:activeUserAll})
              commit('setParticipation',rate)
              // dispatch('getChart',value)
            }

        })

      })

      },
      async getOverAll({commit,rootState,dispatch,state}:any, value:any){
        let activeUser:any=[]
        let givenUser:any=[]
        let past_activeUser:any=[]
        let past_givenUser:any=[]
        const data:any={
          totalRecognition:0,
          chartRecognition:[],
          labels:[],
          pasttotalRecognition:0,
          compareRecognition:'',
          value_aligned:0,
          pastvalue_aligned:0,
          comparevalue_aligned:'',
          chartvalue_aligned:[],
          percent_value_aligned:'',
          comparepercent_value_aligned:'',
          chartpercent_value_aligned:[],
          participation:'',
          chartparticipation:[],
          past_participation:''
        };
        let dataChart:any={
          totalRecognition:0,
          labels:[],
          value_aligned:0,
          percent_value_aligned:'',
          activeUser:[],
          givenUser:[],
          participation:'',
        };
        if(value=="Past 30 days"){
          for(let i=0;i<=60;i++){
            let date = format(addDays(new Date(),-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            let active_day=[]
            if(date>=format(addDays(new Date(),-30), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                
                active_day=_.groupBy(state.data_all_day_participation.activeUserAll[date][0].users, function (b) { return b })
          
                //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }else{
              if(state.data_all_day_participation.activeUserAll[date]){
                //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
            }
            if(findData&&date>=format(addDays(new Date(),-30), 'YYYY-MM-DD')){
              
              let given_day=[]
              if(state.data_all_day_participation.givenAll[date]){
                 given_day=_.groupBy(state.data_all_day_participation.givenAll[date][0].users, function (b) { return b })
                 //console.log('state.data_all_day_participation.givenAll[date]',state.data_all_day_participation.givenAll[date][0].users)
                // data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }

              const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
              data.chartparticipation.push(_rate_day)
              data.totalRecognition=data.totalRecognition+findData.activity
              data.chartRecognition.push(findData.activity)
              data.labels.push(date)
              data.value_aligned=data.value_aligned+findData.hashtag_value
              data.chartvalue_aligned.push(findData?'0':(findData.hashtag_value))
              data.chartpercent_value_aligned.push(isNaN((findData.hashtag_value*100)/findData.activity)?'0':((findData.hashtag_value*100)/findData.activity).toFixed())
            }else if(findData){
              
              
              if(state.data_all_day_participation.givenAll[date]){
               // data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(i<=29){
              data.labels.push(date)
              data.chartRecognition.push('0')
              data.chartvalue_aligned.push('0')
              data.chartparticipation.push('0')
              data.chartpercent_value_aligned.push('0')
            
            }
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="Past 90 days"){
          let first_week=''
          let _rate_day='0'
          for(let i=0;i<=180;i++){
            let date = format(addDays(new Date(),-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const week = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=week
            }
            if(date>=format(addDays(new Date(),-90), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }else{
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
            }
            
           
            if(findData&&date>=format(addDays(new Date(),-90), 'YYYY-MM-DD')){
              let active_day=[]
            let given_day=[]
              if(first_week!=week){
              active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
              given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
              _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
              data.chartvalue_aligned.push(dataChart.value_aligned)
              data.chartRecognition.push(dataChart.totalRecognition)
              data.chartparticipation.push(_rate_day)
              data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
              data.labels.push(first_week)
              first_week=week
              dataChart={
                totalRecognition:0,
                labels:[],
                value_aligned:0,
                percent_value_aligned:'',
                activeUser:[],
                givenUser:[],
                participation:'',
              };
              }
              
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
               
              
            }else if(findData){
              // if(state.data_all_day_participation.activeUserAll[date]){
              //   //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
              //   past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              // }
              if(state.data_all_day_participation.givenAll[date]){
                //data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(i<90){
              if(first_week!=week){
       
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                _rate_day='0'
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
             
              }
            }
          }
          // const given=_.groupBy(givenUser, function (b) { return b })
          // const active=_.groupBy(activeUser, function (b) { return b })
          // const _given=_.groupBy(past_givenUser, function (b) { return b })
          // const _active=_.groupBy(past_activeUser, function (b) { return b })
          // const _rate= (Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          // const rate= (Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          // data.participation=rate
          // data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          // data.compareRecognition=(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          // data.comparevalue_aligned=(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          // data.percent_value_aligned=(((data.value_aligned*100)/data.totalRecognition).toFixed())
          // data.comparepercent_value_aligned=(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="Past 180 days"){

          let first_week=''
          let week=''
          for(let i=0;i<=360;i++){
            let date = format(addDays(new Date(),-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const weekData = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=format(addWeeks(new Date(date),-1), 'YYYY-W')
              week=format(new Date(date), 'YYYY-W')
            }
            let active_day=[]
            let given_day=[]
            if(date>=format(addDays(new Date(),-180), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }else{
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
            }




            if(findData&&date>=format(addDays(new Date(),-180), 'YYYY-MM-DD')){
              //bi-weekly
              if(first_week!=weekData&&week!=weekData){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(week)
                first_week=format(addWeeks(new Date(date),-1), 'YYYY-W')
                week=format(new Date(date), 'YYYY-W')
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
              // if(state.data_all_day_participation.activeUserAll[date]){
                
              //   dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              //   activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              // }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
              
  
               

               
              
            }else if(findData){
              // if(state.data_all_day_participation.activeUserAll[date]){
              //   //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
              //   past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              // }
              if(state.data_all_day_participation.givenAll[date]){
                //data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(i<180){
              if(first_week!=weekData&&week!=weekData){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(week)
                first_week=format(addWeeks(new Date(date),-1), 'YYYY-W')
                week=format(new Date(date), 'YYYY-W')
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
            }
          }
          // const given=_.groupBy(givenUser, function (b) { return b })
          // const active=_.groupBy(activeUser, function (b) { return b })
          // const _given=_.groupBy(past_givenUser, function (b) { return b })
          // const _active=_.groupBy(past_activeUser, function (b) { return b })
          // const _rate= (Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          // const rate= (Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          // data.participation=rate
          // data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          // data.compareRecognition=(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          // data.comparevalue_aligned=(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          // data.percent_value_aligned=(((data.value_aligned*100)/data.totalRecognition).toFixed())
          // data.comparepercent_value_aligned=(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="This Quarter"){
          
          const pastQuarter = format(addQuarters(new Date(),-1), 'YYYY-Q')
          const splitQuarter:any = pastQuarter.split('-')
          const thisquarter = format(new Date(), 'YYYY-Q')
          let firstDaypastquarter=''
          if(splitQuarter[1]==1){
            const date=`${pastQuarter.split('-')[0]}-01-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==2){
            const date=`${pastQuarter.split('-')[0]}-04-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==3){
            const date=`${pastQuarter.split('-')[0]}-07-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==4){
            const date=`${pastQuarter.split('-')[0]}-10-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          
          const firstDayquarter = format(startOfQuarter(new Date()), 'YYYY-MM-DD')
          let Difference_In_Time =new Date().getTime()-new Date(firstDaypastquarter).getTime() 
          let Difference_In_Days =Math.round(Difference_In_Time / (1000 * 3600 * 24));
          let first_week=''
          let firstWeekQuarter = format(new Date(firstDayquarter), 'YYYY-W')
          let firstWeekToday = format(new Date(), 'YYYY-W')
          for(let i=0;i<=Difference_In_Days;i++){
            let date = format(addDays(new Date(),-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const week = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=week
            }
            let active_day=[]
            let given_day=[]
           
            if(findData&&date>=format(new Date(firstDayquarter), 'YYYY-MM-DD')){
              
              if(first_week!=week){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day=isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?"0": (Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }else if(firstWeekQuarter==firstWeekToday&&date==format(new Date(firstDayquarter), 'YYYY-MM-DD')){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                for(let i=1;i<12;i++){
                  const subweek=format(addWeeks(new Date(), -i), 'YYYY-W')
                  data.labels.push(subweek)
                  data.chartvalue_aligned.push('0')
                  data.chartRecognition.push('0')
                  data.chartparticipation.push('0')
                  data.chartpercent_value_aligned.push('0')
  
                }
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
              
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
              
             
               

               
              
            }else if(findData){
              if(state.data_all_day_participation.activeUserAll[date]){
                //console.log('state.data_all_day_participation.activeUserAll[date]',state.data_all_day_participation.activeUserAll[date][0].users)
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(state.data_all_day_participation.givenAll[date]){
                //data.givenUser.push(...state.data_all_day_participation.givenAll[date].users)
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(date>=format(new Date(firstDayquarter), 'YYYY-MM-DD')){
              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(first_week!=week){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }else if(firstWeekQuarter==firstWeekToday&&date==format(new Date(firstDayquarter), 'YYYY-MM-DD')){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
                for(let i=1;i<12;i++){
                  const subweek=format(addWeeks(new Date(), -i), 'YYYY-W')
                  data.labels.push(subweek)
                  data.chartvalue_aligned.push('0')
                  data.chartRecognition.push('0')
                  data.chartparticipation.push('0')
                  data.chartpercent_value_aligned.push('0')
  
                }
              }
             
              
            
            }else {
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }
            
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()

        }else if(value=="Last Quarter"){
          const pastQuarter = format(addQuarters(new Date(),-1), 'YYYY-Q')
          const splitQuarter:any = pastQuarter.split('-')
          let firstDaypastquarter=''
          let lastDaypastquarter=''
          if(splitQuarter[1]==1){
            const date=`${pastQuarter.split('-')[0]}-01-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-03-31`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==2){
            const date=`${pastQuarter.split('-')[0]}-04-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-06-30`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==3){
            const date=`${pastQuarter.split('-')[0]}-07-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-09-30`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          else if(splitQuarter[1]==4){
            const date=`${pastQuarter.split('-')[0]}-10-01`
            firstDaypastquarter = format(new Date(date), 'YYYY-MM-DD')
            const _date=`${pastQuarter.split('-')[0]}-12-31`
            lastDaypastquarter = format(new Date(_date), 'YYYY-MM-DD')
          }
          const pastlastQuarter = format(addQuarters(new Date(),-2), 'YYYY-Q')
          const splitlastQuarter:any = pastlastQuarter.split('-')
          let firstDaypastlastquarter=''
          if(splitlastQuarter[1]==1){
            const date=`${pastlastQuarter.split('-')[0]}-01-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitlastQuarter[1]==2){
            const date=`${pastlastQuarter.split('-')[0]}-04-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitlastQuarter[1]==3){
            const date=`${pastlastQuarter.split('-')[0]}-07-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }
          else if(splitlastQuarter[1]==4){
            const date=`${pastlastQuarter.split('-')[0]}-10-01`
            firstDaypastlastquarter = format(new Date(date), 'YYYY-MM-DD')
          }

          let Difference_In_Time =new Date(lastDaypastquarter).getTime()-new Date(firstDaypastlastquarter).getTime() 
          let Difference_In_Days =Math.round(Difference_In_Time / (1000 * 3600 * 24));

          let first_week=''
          for(let i=0;i<=Difference_In_Days;i++){
            let date = format(addDays(new Date(lastDaypastquarter ),-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const week = format(new Date(date), 'YYYY-W')
            if(first_week==''){
              first_week=week
            }
            
            
            if(findData&&date>=format(new Date(firstDaypastquarter), 'YYYY-MM-DD')){

              let active_day=[]
              let given_day=[]
              if(first_week!=week){
                active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }

              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value

            }else if(findData){
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(state.data_all_day_participation.givenAll[date]){
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(date>=format(new Date(firstDaypastquarter), 'YYYY-MM-DD')){

              if(state.data_all_day_participation.activeUserAll[date]){
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(first_week!=week){
                const  active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                const given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_week)
                first_week=week
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
              }
            
            }else {
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
            }
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()
        }else if(value=="Past year"){
          let first_MM=''
          for(let i=0;i<=730;i++){
            let date = format(addDays(new Date(),-i), 'YYYY-MM-DD')
            const findData=_.find(state.data_all_day, { date:date});
            const MM = format(new Date(date), 'YYYY-MM')
            if(first_MM==''){
              first_MM=MM
            }
            
            if(findData&&date>=format(addDays(new Date(),-365), 'YYYY-MM-DD')){
              let active_day=[]
              let given_day=[]
              if(first_MM!=MM){
              active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
              given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
              const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
              data.chartvalue_aligned.push(dataChart.value_aligned)
              data.chartRecognition.push(dataChart.totalRecognition)
              data.chartparticipation.push(_rate_day)
              data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
              data.labels.push(first_MM)
              first_MM=MM
              dataChart={
                totalRecognition:0,
                labels:[],
                value_aligned:0,
                percent_value_aligned:'',
                activeUser:[],
                givenUser:[],
                participation:'',
              };
              }
              if(state.data_all_day_participation.activeUserAll[date]){
                
                dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
                activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              
              if(state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }
              
              
              data.totalRecognition=data.totalRecognition+findData.activity
              data.value_aligned=data.value_aligned+findData.hashtag_value
              
              dataChart.totalRecognition=dataChart.totalRecognition+findData.activity
              dataChart.value_aligned=dataChart.value_aligned+findData.hashtag_value
               
              
            }else if(findData){
              if(state.data_all_day_participation.activeUserAll[date]){
                past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users)
              }
              if(state.data_all_day_participation.givenAll[date]){
                past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users)
              }


              data.pasttotalRecognition=data.pasttotalRecognition+findData.activity
              data.pastvalue_aligned=data.pastvalue_aligned+findData.hashtag_value
            }else if(date>=format(addDays(new Date(),-365), 'YYYY-MM-DD')){
              if(first_MM!=MM){
                const active_day=_.groupBy(dataChart.activeUser, function (b) { return b })
                const given_day=_.groupBy(dataChart.givenUser, function (b) { return b })
                const _rate_day= isNaN(Object.keys(given_day).length*100/ Object.keys(active_day).length)?'0':(Object.keys(given_day).length*100/ Object.keys(active_day).length).toFixed()
                data.chartvalue_aligned.push(dataChart.value_aligned)
                data.chartRecognition.push(dataChart.totalRecognition)
                data.chartparticipation.push(_rate_day)
                data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition)?'0':((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed())
                data.labels.push(first_MM)
                first_MM=MM
                dataChart={
                  totalRecognition:0,
                  labels:[],
                  value_aligned:0,
                  percent_value_aligned:'',
                  activeUser:[],
                  givenUser:[],
                  participation:'',
                };
                }
            }
          }
          const given=_.groupBy(givenUser, function (b) { return b })
          const active=_.groupBy(activeUser, function (b) { return b })
          const _given=_.groupBy(past_givenUser, function (b) { return b })
          const _active=_.groupBy(past_activeUser, function (b) { return b })
          const _rate= isNaN(Object.keys(_given).length*100/ Object.keys(_active).length)?'0':(Object.keys(_given).length*100/ Object.keys(_active).length).toFixed()
          const rate= isNaN(Object.keys(given).length*100/ Object.keys(active).length)?'0':(Object.keys(given).length*100/ Object.keys(active).length).toFixed()
          data.participation=rate
          data.past_participation=(parseFloat(rate)-parseFloat(_rate)).toFixed(0)
          data.compareRecognition=data.pasttotalRecognition==0?'0':(((data.totalRecognition-data.pasttotalRecognition)*100)/data.pasttotalRecognition).toFixed()
          data.comparevalue_aligned=data.pastvalue_aligned==0?'0':isNaN(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned)?'0':(((data.value_aligned-data.pastvalue_aligned)*100)/data.pastvalue_aligned).toFixed()
          data.percent_value_aligned=isNaN(((data.value_aligned*100)/data.totalRecognition))?'0':(((data.value_aligned*100)/data.totalRecognition).toFixed())
          data.comparepercent_value_aligned=isNaN(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition))?'0':(parseFloat(data.percent_value_aligned)-((data.pastvalue_aligned*100)/data.pasttotalRecognition)).toFixed()

          

        }
        data.labels.reverse()
        data.chartRecognition.reverse()
        data.chartvalue_aligned.reverse()
        data.chartpercent_value_aligned.reverse()
        data.chartparticipation.reverse()
        commit('setDataOverAll',data)
        commit('setChartLoading',false) 
      },
      async getOverAllCustomRange({commit,rootState,dispatch,state}:any, value:any){
        const { startDate, endDate } = value;
        
        let activeUser:any=[]
        let givenUser:any=[]
        let past_activeUser:any=[]
        let past_givenUser:any=[]
        const data:any={
          totalRecognition:0,
          chartRecognition:[],
          labels:[],
          pasttotalRecognition:0,
          compareRecognition:'',
          value_aligned:0,
          pastvalue_aligned:0,
          comparevalue_aligned:'',
          chartvalue_aligned:[],
          percent_value_aligned:'',
          comparepercent_value_aligned:'',
          chartpercent_value_aligned:[],
          participation:'',
          chartparticipation:[],
          past_participation:''
        };
        let dataChart:any={
          totalRecognition:0,
          labels:[],
          value_aligned:0,
          percent_value_aligned:'',
          activeUser:[],
          givenUser:[],
          participation:'',
        };
        
        // Calculate the date range
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Calculate the comparison period (same duration before the start date)
        const pastEnd = new Date(start);
        pastEnd.setDate(pastEnd.getDate() - 1);
        const pastStart = new Date(pastEnd);
        pastStart.setDate(pastStart.getDate() - diffDays);
        
        // Iterate through all days from past period start to current period end
        const totalDaysToProcess = diffDays * 2 + 2;
        const currentPeriodStart = format(start, 'YYYY-MM-DD');
        const currentPeriodEnd = format(end, 'YYYY-MM-DD');
        const pastPeriodStart = format(pastStart, 'YYYY-MM-DD');
        const pastPeriodEnd = format(pastEnd, 'YYYY-MM-DD');
        
        // Group data by month for chart display
        let first_month = '';
        
        // Process all dates in both periods
        for(let i = totalDaysToProcess; i >= 0; i--){
          let date = format(addDays(end, -i), 'YYYY-MM-DD');
          const findData = _.find(state.data_all_day, { date: date });
          const month = format(new Date(date), 'YYYY-MM');
          
          if(first_month === ''){
            first_month = month;
          }
          
          // Check if date is in current period
          const isCurrentPeriod = date >= currentPeriodStart && date <= currentPeriodEnd;
          // Check if date is in past period
          const isPastPeriod = date >= pastPeriodStart && date <= pastPeriodEnd;
          
          if(isCurrentPeriod){
            if(state.data_all_day_participation && state.data_all_day_participation.activeUserAll && state.data_all_day_participation.activeUserAll[date]){
              dataChart.activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users);
              activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users);
            }
            
            // Check if we've moved to a new month
            if(first_month !== month){
              const active_day = _.groupBy(dataChart.activeUser, function (b) { return b });
              const given_day = _.groupBy(dataChart.givenUser, function (b) { return b });
              const _rate_day = isNaN(Object.keys(given_day).length*100 / Object.keys(active_day).length) ? '0' : (Object.keys(given_day).length*100 / Object.keys(active_day).length).toFixed();
              data.chartvalue_aligned.push(dataChart.value_aligned);
              data.chartRecognition.push(dataChart.totalRecognition);
              data.chartparticipation.push(_rate_day);
              data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition) ? '0' : ((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed());
              data.labels.push(first_month);
              first_month = month;
              dataChart = {
                totalRecognition: 0,
                labels: [],
                value_aligned: 0,
                percent_value_aligned: '',
                activeUser: [],
                givenUser: [],
                participation: '',
              };
            }
            
            if(findData){
              if(state.data_all_day_participation && state.data_all_day_participation.givenAll && state.data_all_day_participation.givenAll[date]){
                dataChart.givenUser.push(...state.data_all_day_participation.givenAll[date][0].users);
                givenUser.push(...state.data_all_day_participation.givenAll[date][0].users);
              }
              
              data.totalRecognition = data.totalRecognition + findData.activity;
              data.value_aligned = data.value_aligned + findData.hashtag_value;
              
              dataChart.totalRecognition = dataChart.totalRecognition + findData.activity;
              dataChart.value_aligned = dataChart.value_aligned + findData.hashtag_value;
            }
          } else if(isPastPeriod && findData){
            if(state.data_all_day_participation && state.data_all_day_participation.activeUserAll && state.data_all_day_participation.activeUserAll[date]){
              past_activeUser.push(...state.data_all_day_participation.activeUserAll[date][0].users);
            }
            if(state.data_all_day_participation && state.data_all_day_participation.givenAll && state.data_all_day_participation.givenAll[date]){
              past_givenUser.push(...state.data_all_day_participation.givenAll[date][0].users);
            }
            
            data.pasttotalRecognition = data.pasttotalRecognition + findData.activity;
            data.pastvalue_aligned = data.pastvalue_aligned + findData.hashtag_value;
          }
        }
        
        // Push the last month's data
        if(dataChart.totalRecognition > 0 || dataChart.value_aligned > 0){
          const active_day = _.groupBy(dataChart.activeUser, function (b) { return b });
          const given_day = _.groupBy(dataChart.givenUser, function (b) { return b });
          const _rate_day = isNaN(Object.keys(given_day).length*100 / Object.keys(active_day).length) ? '0' : (Object.keys(given_day).length*100 / Object.keys(active_day).length).toFixed();
          data.chartvalue_aligned.push(dataChart.value_aligned);
          data.chartRecognition.push(dataChart.totalRecognition);
          data.chartparticipation.push(_rate_day);
          data.chartpercent_value_aligned.push(isNaN((dataChart.value_aligned*100)/dataChart.totalRecognition) ? '0' : ((dataChart.value_aligned*100)/dataChart.totalRecognition).toFixed());
          data.labels.push(first_month);
        }
        
        // Calculate participation rates
        const given = _.groupBy(givenUser, function (b) { return b });
        const active = _.groupBy(activeUser, function (b) { return b });
        const _given = _.groupBy(past_givenUser, function (b) { return b });
        const _active = _.groupBy(past_activeUser, function (b) { return b });
        const _rate = isNaN(Object.keys(_given).length*100 / Object.keys(_active).length) ? '0' : (Object.keys(_given).length*100 / Object.keys(_active).length).toFixed();
        const rate = isNaN(Object.keys(given).length*100 / Object.keys(active).length) ? '0' : (Object.keys(given).length*100 / Object.keys(active).length).toFixed();
        
        data.participation = rate;
        data.past_participation = (parseFloat(rate) - parseFloat(_rate)).toFixed(0);
        data.compareRecognition = data.pasttotalRecognition == 0 ? '0' : (((data.totalRecognition - data.pasttotalRecognition) * 100) / data.pasttotalRecognition).toFixed();
        data.comparevalue_aligned = data.pastvalue_aligned == 0 ? '0' : isNaN(((data.value_aligned - data.pastvalue_aligned) * 100) / data.pastvalue_aligned) ? '0' : (((data.value_aligned - data.pastvalue_aligned) * 100) / data.pastvalue_aligned).toFixed();
        data.percent_value_aligned = isNaN(((data.value_aligned * 100) / data.totalRecognition)) ? '0' : (((data.value_aligned * 100) / data.totalRecognition).toFixed());
        data.comparepercent_value_aligned = isNaN(parseFloat(data.percent_value_aligned) - ((data.pastvalue_aligned * 100) / data.pasttotalRecognition)) ? '0' : (parseFloat(data.percent_value_aligned) - ((data.pastvalue_aligned * 100) / data.pasttotalRecognition)).toFixed();
        
        // Reverse arrays to show chronological order
        data.labels.reverse();
        data.chartRecognition.reverse();
        data.chartvalue_aligned.reverse();
        data.chartpercent_value_aligned.reverse();
        data.chartparticipation.reverse();
        
        commit('setDataOverAll', data);
        commit('setChartLoading', false);
      }

      
    },
    getters: {
     activity : (state:any) => {
      
        return state.activity
      },
      activityall : (state:any) => {
        return state.activityall
      },
      results : (state:any) => {
        return state.results
      },
      sizepage: (state:any) => {
        return state.size_page
      },
      page : (state:any) => {
        return state.page
      },
      list_month: (state: any) => {
        return state.list_month
      },
      reportActivity:(state: any) => {
        return state.reportActivity
      },
      barchart:(state: any) => {
        return state.barchart
      },
      chartLoading:(state: any) => {
        return state.chartLoading
      },
      radarchart:(state: any) => {
        return state.radarchart
      },
      listChartmonth:(state: any) => {
        return state.listChartmonth
      },
      rewardTypeOptions:(state: any) => {
        return state.medal
      },
      dataOverAll:(state: any) => {
        return state.dataOverAll
      },
      radarchartAll:(state: any) => {
        return state.radarchartAll
      }
    }
  }